import { useState } from 'react';
import {
  getAvailabilityStatus,
  processAvailabilityForDay,
  TimeSlot,
} from '../../utils/restaurant-availability';
import ToggleNav from '../../components/ToggleNav';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';

const RestaurantMenuHeader = ({
  restaurant,
  activeGroup,
  setActiveGroup,
  onViewOpeningTimesOpen,
  fromKiosk = false,
}) => {
  const { language } = useDataContext();
  const isOpen = getAvailabilityStatus(restaurant, { type: 'boolean' });

  const selectedGroups = [
    { id: 'order-now', label: translations[language]?.order_now },
    { id: 'information', label: translations[language]?.information },
  ];

  const days = [
    { key: 'Mon', label: translations[language]?.monday },
    { key: 'Tue', label: translations[language]?.tuesday },
    { key: 'Wed', label: translations[language]?.wednesday },
    { key: 'Thu', label: translations[language]?.thursday },
    { key: 'Fri', label: translations[language]?.friday },
    { key: 'Sat', label: translations[language]?.saturday },
    { key: 'Sun', label: translations[language]?.sunday },
  ];

  const availability = processAvailabilityForDay(
    restaurant?.availability,
    days,
    new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(new Date()),
    new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(new Date()),
  );
  const timeSlot = availability?.timeSlots?.[0] || '';
  const isAvailable =
    isOpen ||
    (availability?.isAvailable ? Boolean(availability?.timeSlots?.[0] || false) || false : false);

  return (
    <div className="relative rounded-t-[16px] overflow-hidden mb-1 sm:!mb-6">
      <img
        src={restaurant.cover}
        alt="Restaurant"
        className="w-full h-[400px] lg:h-[500px] object-cover"
      />
      <div
        className="absolute inset-0 flex flex-col justify-between text-white"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      >
        <div
          className={`h-full p-4 pb-4 lg:p-8 lg:pb-4 flex flex-col ${
            fromKiosk ? 'justify-end pb-4 lg:pb-8' : 'justify-between'
          }`}
        >
          {!fromKiosk && (
            <div className="flex justify-between items-start gap-1">
              <div className="flex justify-start items-start">
                <div className="flex justify-center items-center min-w-9 min-h-9 bg-gray-100 rounded-full mr-2">
                  <i className="fas fa-map-marker-alt text-gray-600" />
                </div>
                <div>
                  <p className="text-xs text-primary">
                    {restaurant.distance || '0 m'} {translations[language]?.restaurant_from_you}
                  </p>
                  <p className="text-xs text-white">{restaurant.address}</p>
                </div>
              </div>
              <p
                className={`restaurant-status px-7 py-2 text-sm text-white font-semibold rounded-full ${
                  !isOpen && 'closed'
                }`}
              >
                {isOpen
                  ? translations[language]?.restaurant_open
                  : translations[language]?.restaurant_closed}
              </p>
            </div>
          )}
          <h1 className="text-2xl lg:text-[40px] font-bold">{restaurant.name}</h1>
        </div>

        {!fromKiosk && (
          <div
            className="flex flex-col-reverse lg:flex-row items-center justify-between p-4 pt-4 lg:p-8 lg:pt-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          >
            <ToggleNav
              className="w-full max-w-[280px] sm:max-w-[330px]"
              options={selectedGroups}
              activeId={activeGroup}
              onChange={setActiveGroup}
            />

            {!fromKiosk && (
              <div className="flex flex-col sm:flex-row items-center mb-2 lg:mb-0">
                <p className="text-sm text-white font-semibold mr-0 sm:!mr-4 mb-2 sm:!mb-0">
                  {isAvailable ? (
                    <>
                      <span>{translations[language]?.restaurant_open_today}: </span>
                      <span>
                        <TimeSlot start={timeSlot.start} end={timeSlot.end} />
                      </span>
                    </>
                  ) : null}
                </p>
                <button
                  className="bg-primary hover:bg-secondary transition-colors text-sm text-white font-semibold px-3 sm:px-4 py-[8px] rounded-[30px]"
                  onClick={onViewOpeningTimesOpen}
                >
                  {translations[language]?.restaurant_menu_header_view_opening_times}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantMenuHeader;
